/**
 * Copyright Open Logistics Foundation
 *
 * Licensed under the Open Logistics Foundation License 1.3.
 * For details on the licensing terms, see the LICENSE file.
 * SPDX-License-Identifier: OLFL-1.3
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EcmrPage } from '@app/shared/models/ecmr/EcmrPage';
import { EcmrRole } from '@app/core/enums/ecmr/EcmrRole';
import { EcmrType } from '@app/shared/models/ecmr/EcmrType';
import { EcmrTransportType } from '@app/shared/models/ecmr/EcmrTransportType';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { EcmrShare } from '@app/shared/models/ecmr/EcmrShare';
import { ShowColumns } from '@app/shared/models/ecmr/show-columns';
import { FilterRequest } from '@app/shared/models/ecmr/filter-request';
import { EcmrUrlResolverService } from './ecmr-url-resolver.service';

@Injectable({
    providedIn: 'root',
})
export class EcmrService {
    constructor(
        private http: HttpClient,
        private ecmrUrlResolver: EcmrUrlResolverService
    ) {}

    getAllEcmr(
        filterRequest: FilterRequest,
        organizationSite: string,
        ecmrType: EcmrType,
        page: number,
        size: number,
        sortBy: string | null,
        sortingOrder: string
    ) {
        let params: HttpParams = new HttpParams()
            .set('organizationSite', organizationSite)
            .set('type', ecmrType)
            .set('page', page)
            .set('size', size)
            .set('sortingOrder', sortingOrder);
        if (sortBy) params = params.set('sortBy', sortBy);
        return this.http.post<EcmrPage>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/my-ecmrs`,
            filterRequest,
            { params: params }
        );
    }

    getShareToken(siteId: string, ecmrId: string, role: EcmrRole) {
        return this.http.get(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${siteId}/${ecmrId}/share-token`,
            { params: { ecmrRole: role }, responseType: 'text' }
        );
    }

    saveDisplayedColumns(columns: string[]) {
        const columnString: string = JSON.stringify(columns);
        localStorage.setItem('columns', columnString);
    }

    saveShowColumns(showColumns: ShowColumns) {
        const columnString: string = JSON.stringify(showColumns);
        localStorage.setItem('showColumns', columnString);
    }

    saveFilterRequest(filterRequest: FilterRequest) {
        const filterString: string = JSON.stringify(filterRequest);
        sessionStorage.setItem('filterRequest', filterString);
    }

    getDisplayedColumns(): string[] | null {
        const columnString: string | null = localStorage.getItem('columns');
        if (columnString) {
            return JSON.parse(columnString);
        } else {
            return null;
        }
    }

    getShowColumns(): ShowColumns | null {
        const columnString: string | null = localStorage.getItem('showColumns');
        if (columnString) {
            return JSON.parse(columnString);
        } else {
            return null;
        }
    }

    getFilterRequest(): FilterRequest | null {
        const filterString: string | null =
            sessionStorage.getItem('filterRequest');
        if (filterString) {
            return JSON.parse(filterString);
        } else {
            return null;
        }
    }

    getTransportType(ecmr: Ecmr) {
        if (!ecmr) {
            return null;
        }
        const senderCountryCode =
            ecmr.ecmrConsignment.senderInformation.senderCountryCode.value;
        const consigneeCountryCode =
            ecmr.ecmrConsignment.consigneeInformation.consigneeCountryCode
                .value;
        if (!senderCountryCode || !consigneeCountryCode) {
            return null;
        }
        return senderCountryCode === consigneeCountryCode
            ? EcmrTransportType.National
            : EcmrTransportType.International;
    }

    moveToArchive(ecmrId: string) {
        return this.http.patch<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}/archive`,
            {},
            {}
        );
    }

    moveOutOfArchive(ecmrId: string) {
        return this.http.patch<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}/reactivate`,
            {},
            {}
        );
    }

    downloadPdf(ecmrId: string, language: string) {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/pdf');
        return this.http.get(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}/pdf`,
            {
                headers,
                responseType: 'blob',
                observe: 'response',
                params: { language },
            }
        );
    }

    deleteEcmr(ecmrId: string, siteId: string) {
        return this.http.delete(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}`,
            {
                params: { siteId: siteId },
            }
        );
    }

    shareEcmr(siteId: string, ecmrShare: EcmrShare, ecmrId: string) {
        return this.http.patch(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${siteId}/${ecmrId}/share`,
            ecmrShare
        );
    }

    getEcmrAssignmentsDoneByOrganizationSite(ecmrId: string, siteId: string) {
        return this.http.get<string[]>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${siteId}/${ecmrId}/assignments`,
            { responseType: 'json' }
        );
    }

    importEcmr(ecmrId: string) {
        return this.http.get<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}/import`
        );
    }

    getEcmrRolesForCurrentUser(ecmrId: string, siteId: string) {
        return this.http.get<EcmrRole[]>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/${ecmrId}/${siteId}/role`
        );
    }

    importPdfCmr(file: File, siteId: string) {
        const formData = new FormData();
        formData.append('document', file);

        return this.http.post<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/ecmr/organizationSite/${siteId}/importPdf`,
            formData
        );
    }
}

export interface EcmrElement {
    id: string;
    referenceId: string;
    from: string;
    to: string;
    transportType: TransportType;
    lastEditor: string;
    status: Status;
    lastEditDate: string;
    creationDate: string;
}

enum Status {
    NEW = 'New',
    LOADING = 'Loading',
    IN_TRANSPORT = 'In Transport',
    DELIVERED = 'Delivered',
}

enum TransportType {
    National = 'national',
    International = 'international',
}
